import { Observable, Subscription } from 'rxjs'
import { NotificationService } from 'src/app/core/notification.service'
import {
  AvailableCountry,
  TranslatorService
} from 'src/app/core/translator.service'
import { AddressDetails } from 'src/app/shared/components/address-autocomplete/address-details'
import {
  isValidFieldConfigJSON,
  scrollToFirstInvalidControl
} from 'src/app/shared/utils'
import { PhoneValidator } from 'src/app/shared/validators/phone.validator'
import { environment } from 'src/environments/environment'

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'

import { Organization } from '../organization'
import { EmailAddressValidator } from 'src/app/shared/validators/email-address.validator'
import { filter, map, tap } from 'rxjs/operators'
import { ConfigurationOption } from '../../configuration-option/configuration-option'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/core/core.state'
import {
  FormFieldConfig,
  FormFieldConfigValidatorResult
} from 'src/app/shared/interfaces'
import { ZipcodeValidator } from 'src/app/shared/validators/zipcode.validator'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { organizationSelectors } from '../organization.selectors'
import { organizationActions } from '../organization.actions'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { SelectableItem } from 'src/app/shared/pipes/map-to-selectable-items.pipe'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'organization-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class OrganizationFormComponent implements OnInit, OnDestroy {
  @Output()
  onSubmit = new EventEmitter<any>()
  @Input()
  organization: Organization

  public form: UntypedFormGroup
  public availableCountries: AvailableCountry[]
  public configOption$: Observable<ConfigurationOption>
  public organizationTypes$: Observable<any>
  public isSecondaryOrgCodeRequired: boolean = false
  public countryCallingCode = environment.countryCallingCode
  public formFieldConfigs$: Observable<FormFieldConfig[]>
  public missingFormConfig: boolean = true
  public formConfigValidatorResult: FormFieldConfigValidatorResult
  public formFieldConfigObjects: any
  public selectedLanguage$: Observable<string>
  public organizations$: Observable<Organization[]>
  public isLoading$: Observable<boolean>

  private subscriptions: Subscription[] = []

  private fieldNames: string[] = [
    'description',
    'additionalDescription',
    'type',
    'email',
    'phone',
    'commercialCode',
    'website',
    'organisationSecondaryCode',
    'street',
    'streetNumber',
    'postbox',
    'city',
    'zipCode',
    'country',
    'parentOrganisationId',
    'organisationCode',
    'tertiaryCode',
    'quaternaryCode',
    'quintaryCode'
  ]

  constructor(
    private fb: UntypedFormBuilder,
    private translatorService: TranslatorService,
    private translate: TranslateService,
    private elementRef: ElementRef,
    private notificationService: NotificationService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(organizationSelectors.selectIsLoading)

    this.availableCountries = environment.availableCountries.sort((a, b) =>
      a.name.localeCompare(b.name)
    )

    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.formFieldConfigObjects = {}

    this.formFieldConfigs$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        'organisation_form_config'
      )
      .pipe(
        filter((configurationOption) => Boolean(configurationOption)),
        map((configurationOption) => JSON.parse(configurationOption.value)),
        tap((configurationOption) => {
          for (let config of configurationOption) {
            this.formFieldConfigObjects[config.inputName] = config
          }

          if (!this.formFieldConfigObjects['additionalDescription']) {
            this.formFieldConfigObjects['additionalDescription'] = {
              inputName: 'additionalDescription',
              title_en: '',
              title_nl: '',
              title_fr: '',
              title_de: '',
              placeholder_en: '',
              placeholder_nl: '',
              placeholder_fr: '',
              placeholder_de: '',
              isVisible: false,
              isRequired: false,
              isEditable: true
            }
          }

          this.formConfigValidatorResult = isValidFieldConfigJSON(
            this.formFieldConfigObjects,
            this.fieldNames
          )
          if (!this.formConfigValidatorResult.isValid) {
            return
          }

          this.setFormWithConfigs()
          this.missingFormConfig = false
        })
      )

    this.form = this.fb.group({
      description: [null, []],
      additionalDescription: [null, []],
      type: [null, []],
      street: [null, []],
      streetNumber: [null, []],
      postbox: [null, []],
      city: [null, []],
      zipCode: [null, [ZipcodeValidator()]],
      country: [null, []],
      commercialCode: [null, []],
      website: [null, []],
      organisationCode: [null, []],
      organisationSecondaryCode: [null, []],
      phone: [null, [PhoneValidator()]],
      email: [null, [EmailAddressValidator()]],
      parentOrganisationId: [null, []],
      tertiaryCode: [null, []],
      quaternaryCode: [null, []],
      quintaryCode: [null, []]
    })

    this.subscriptions.push(
      this.form.controls.type.valueChanges.subscribe((selectedType) => {
        if (selectedType == 'Onderwijsinstelling') {
          this.form.controls.organisationSecondaryCode.setValidators([
            Validators.required
          ])
          this.isSecondaryOrgCodeRequired = true
        } else {
          this.form.controls.organisationSecondaryCode.setValidators([])
          this.form.controls.organisationSecondaryCode.clearValidators()
          this.isSecondaryOrgCodeRequired = false
        }
        this.form.controls.organisationSecondaryCode.updateValueAndValidity()
      })
    )

    this.configOption$ = this.store.select(
      configurationOptionSelectors.selectByKey,
      'organisation_types'
    )

    this.organizations$ = this.store
      .select(organizationSelectors.selectAll)
      .pipe(
        map((organizations) => {
          return organizations.filter((organization) => organization.isActive)
        })
      )

    this.organizationTypes$ = this.configOption$.pipe(
      filter((x) => Boolean(x)),
      map((option) => JSON.parse(option.value))
    )
  }

  public getAdditionalDescriptionTitle(language: string | null): string {
    const configuredTitle =
      this.formFieldConfigObjects?.additionalDescription?.[`title_${language}`]

    return (
      (typeof configuredTitle === 'string' && configuredTitle) ||
      this.translate.instant('organizationModule.additionalDescription')
    )
  }

  public onAddressSearch(address: AddressDetails) {
    this.form.controls.street.setValue(address.street)
    this.form.controls.streetNumber.setValue(address.houseNr)
    this.form.controls.city.setValue(address.city)
    this.form.controls.country.setValue(address.country.toLowerCase())
    this.form.controls.zipCode.setValue(address.zipCode)
  }

  public onFormSubmit() {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })

      scrollToFirstInvalidControl(this.elementRef, this.notificationService)
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    let organization = { ...this.form.value }
    const address = {
      street: organization.street,
      streetNumber: organization.streetNumber,
      postbox: organization.postbox,
      city: organization.city,
      zipCode: organization.zipCode,
      country: organization.country == 'xx' ? null : organization.country
    }
    delete organization.street
    delete organization.streetNumber
    delete organization.postbox
    delete organization.city
    delete organization.zipCode
    delete organization.country

    this.onSubmit.emit({ ...organization, addresses: [address] })
  }

  public onSearchOrganization(term: string) {
    this.store.dispatch(
      organizationActions.fetchManyPublic({
        params: RequestQueryBuilder.create({
          join: [{ field: 'addresses' }],
          search: {
            isActive: {
              [CondOperator.EQUALS]: true
            },
            $or: [
              {
                description: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                additionalDescription: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                email: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                commercialCode: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                organisationCode: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                organisationSecondaryCode: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              }
            ]
          }
        })
      })
    )
  }

  private setFormWithConfigs() {
    for (let key of Object.keys(this.form.controls)) {
      if (this.formFieldConfigObjects[key].isRequired) {
        if (key == 'phone' || key == 'mobile') {
          this.form.controls[key].setValidators([
            Validators.required,
            PhoneValidator()
          ])
        } else if (key == 'zipCode') {
          this.form.controls[key].setValidators([
            Validators.required,
            ZipcodeValidator()
          ])
        } else {
          this.form.controls[key].setValidators([Validators.required])
        }
      }

      if (!this.formFieldConfigObjects[key].isEditable) {
        this.form.controls[key].disable()
      }
    }
  }

  public adjustSelectedOrganization(item: SelectableItem[]) {
    this.form.controls.parentOrganisationId.patchValue(item[0]?.value)
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
